import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/shadcn-ui/button';
import { Textarea } from '@/components/shadcn-ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/shadcn-ui/dropdown-menu';
import { Send, Paperclip, Mic, MicOff, X, FileText, Image, ChevronDown, ArrowBigUp, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import DocumentUploadModal from '@/components/DocumentUploadModal';

const MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o' },
  { id: 'gpt-4-turbo', name: 'GPT-4 Turbo' },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
  { id: 'claude-3-opus', name: 'Claude 3 Opus' },
  { id: 'claude-3-sonnet', name: 'Claude 3 Sonnet' },
];

const MIN_LINES = 2;
const LINE_HEIGHT = 24;
const MAX_HEIGHT = 220;

export function ChatInput({ onSendMessage, disabled, showActions = true, onFetchDocuments, onSubmitDocuments, documentsAlreadyUploaded = false }) {
  const textareaRef = useRef(null);
  const [input, setInput] = useState('');
  const [selectedModel, setSelectedModel] = useState(MODELS[0]);
  const [attachments, setAttachments] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const actionsRef = useRef(null);
  const audioChunksRef = useRef([]);
  const MAX_SIZE = 5 * 1024 * 1024;
  const [showDocModal, setShowDocModal] = useState(false);

  const getBottomPadding = () => actionsRef.current?.offsetHeight ?? 64;

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;

    const bottomPadding = getBottomPadding();
    const cursorPosition = el.selectionStart;
    const textBeforeCursor = el.value.substring(0, cursorPosition);
    const currentLine = textBeforeCursor.split('\n').length;

    el.style.height = 'auto';
    el.style.paddingBottom = `${bottomPadding + 16}px`;
    el.style.overflowAnchor = 'none';

    const minHeight = MIN_LINES * LINE_HEIGHT + bottomPadding;
    const maxHeight = MAX_HEIGHT + bottomPadding;

    const newHeight = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);

    el.style.height = `${newHeight}px`;

    if (el.scrollHeight > maxHeight) {
      const cursorLinePosition = currentLine * LINE_HEIGHT;
      const visibleBottom = el.scrollTop + MAX_HEIGHT - bottomPadding - 20;

      if (cursorLinePosition > visibleBottom) {
        el.scrollTop = cursorLinePosition - MAX_HEIGHT + bottomPadding + LINE_HEIGHT * 2;
      }
    }
  };

  useEffect(() => {
    if (!disabled) {
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [disabled]);

  useEffect(() => {
    resizeTextarea();
  }, [input]);

  const handleSubmit = e => {
    e.preventDefault();
    if (!input.trim() && attachments.length > 0) {
      onSendMessage({
        content: '',
        model: selectedModel.id,
        attachments: attachments,
      });
      setAttachments([]);
    }
    if (input.trim() && !disabled) {
      onSendMessage({
        content: input,
        model: selectedModel.id,
        attachments: attachments,
      });
      setInput('');
      requestAnimationFrame(resizeTextarea);
      setAttachments([]);
    }
  };

  const handleFileSelect = e => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    if (files.length > 5) {
      toast.error("You can only upload up to 5 files");
      e.target.value = "";
      return;
    }

    const oversizedFiles = files.filter(file => file.size > MAX_SIZE);

    if (oversizedFiles.length > 0) {
      toast.error("Each file must be 5MB or less");
      e.target.value = "";
      return;
    }

    const newAttachments = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
    e.target.value = "";
  };

  const removeAttachment = id => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = event => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioFile = new File([audioBlob], 'voice-recording.webm', {
          type: 'audio/webm',
        });

        setAttachments(prev => [
          ...prev,
          {
            id: Math.random().toString(36).substr(2, 9),
            name: 'voice-recording.webm',
            size: audioBlob.size,
            type: 'audio/webm',
            file: audioFile,
          },
        ]);

        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = type => {
    if (type.startsWith('image/')) return <Image className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  return (
    <div className="p-4">
      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="mb-2 flex gap-2 overflow-x-auto whitespace-nowrap no-scrollbar">
          {attachments.map(att => (
            <div key={att.id} className="flex items-center gap-2 bg-muted px-3 py-2 rounded-md text-sm">
              {getFileIcon(att.type)}
              <span className="max-w-[150px] truncate">{att.name}</span>
              <span className="text-muted-foreground text-xs">({formatFileSize(att.size)})</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={() => removeAttachment(att.id)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Main Input Container */}
      <div className="relative rounded-[20px] dark text-black bg-white shadow-sm focus-within:ring-1 focus-within:ring-ring">
        <Textarea
          ref={textareaRef}
          value={input}
          placeholder="Type here"
          disabled={disabled}
          className="
            min-h-[120px]
            max-h-[220px]
            w-full
            resize-none
            overflow-y-auto
            border-0
            bg-transparent
            px-4
            pt-4
            focus-visible:ring-0
            focus-visible:ring-offset-0
            no-scrollbar  
            rounded-[20px]
            transition-[height] duration-150 "
          onChange={e => {
            setInput(e.target.value);
            resizeTextarea();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
        />
        {/* Bottom Controls Row */}
        <div
          ref={actionsRef}
          className={`absolute bottom-0 left-3 right-3 flex items-center bg-inherit mb-2 ${showActions ? 'justify-between' : 'justify-end'}`}
        >
          {/* Left Side - Action Buttons and Model Selector */}
          {showActions && (
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                disabled={disabled}
                className="h-9 px-4 rounded-[20px] border-2 border-[#212121] bg-black text-white  flex items-center gap-2"
                onClick={() => {
                  if (documentsAlreadyUploaded) {
                    toast.info('Documents have already been uploaded for this chat.');
                    return;
                  }
                  setShowDocModal(true);
                }}
              >
                <Paperclip className="h-4 w-4 -rotate-45" />
                <span>Attach</span>
              </Button>

              {/* Voice Input Button */}
              {/* <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                className="h-8 w-8"
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? <MicOff className="h-4 w-4 text-red-500 animate-pulse" /> : <Mic className="h-4 w-4" />}
              </Button> */}

              {/* Model Selection Dropdown */}
              {/* <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  disabled={disabled}
                  className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  {selectedModel.name}
                  <ChevronDown className="ml-1 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[200px]">
                {MODELS.map(model => (
                  <DropdownMenuItem
                    key={model.id}
                    onClick={() => setSelectedModel(model)}
                    className={selectedModel.id === model.id ? 'bg-accent' : ''}
                  >
                    {model.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu> */}
            </div>
          )}

          {/* Right Side - Send Button */}
          <Button
            type="submit"
            disabled={disabled || (!input.trim() && attachments.length === 0)}
            onClick={handleSubmit}
            size="custom"
            className="p-3 rounded-[20px] bg-black hover:bg-[#2a2a2a] text-white flex items-center justify-center"
          >
            <span className="flex items-center justify-center gap-2">
              <ArrowUp className="text-white" />
              Send
            </span>
          </Button>
        </div>
      </div>

      {/* Recording Indicator */}
      {isRecording && (
        <div className="mt-2 text-sm text-red-500 flex items-center gap-2">
          <span className="animate-pulse">●</span>
          Recording... Click mic again to stop
        </div>
      )}

      {onFetchDocuments && onSubmitDocuments && (
        <DocumentUploadModal
          isOpen={showDocModal}
          onClose={() => setShowDocModal(false)}
          onFetchDocuments={onFetchDocuments}
          onSubmitDocuments={onSubmitDocuments}
        />
      )}
    </div>
  );
}
